import { useCallback, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getCategories, getDishes } from "@/api/Dishes/DishesAPI";
import type { Dish, DishCategory } from "@/api/Dishes/DishesAPI.model";
import { getMenuTypes } from "@/api/Reference/ReferenceAPI";
import type { MenuType } from "@/api/Reference/ReferenceAPI.model";
import { getDishSizes } from "@/api/Sizes/SizesAPI";
import type { DishSize } from "@/api/Sizes/SizesAPI.model";
import { getToppings } from "@/api/Toppings/ToppingsAPI";
import type { Topping } from "@/api/Toppings/ToppingsAPI.model";
import { getTranslation } from "@/helpers/getTranslation";
import { queryKeys } from "@/lib/queryKeys";

export interface MenuCategoryGroup {
  category: DishCategory;
  categoryName: string;
  /** The category's own photo. Absent until the backend supports one. */
  imageUrl?: string;
  dishes: Dish[];
}

export interface MenuTypeGroup {
  menuType: MenuType;
  name: string;
  dishes: Dish[];
  /** Only the categories that this menu type's dishes actually fall into. */
  categories: MenuCategoryGroup[];
}

/**
 * Shared empties. A fresh `[]` on every render would change identity and defeat
 * the memos below, which is the whole point of caching the data.
 */
const EMPTY_DISHES: Dish[] = [];
const EMPTY_CATEGORIES: DishCategory[] = [];
const EMPTY_TOPPINGS: Topping[] = [];
const EMPTY_SIZES: DishSize[] = [];
const EMPTY_TYPES: MenuType[] = [];

/** Categories that hold at least one of the given dishes, in venue order. */
const buildCategoryGroups = (
  categories: DishCategory[],
  dishes: Dish[],
  language: string,
): MenuCategoryGroup[] =>
  categories
    .map((category) => ({
      category,
      categoryName: getTranslation(category.translations, language),
      imageUrl: category.image?.url,
      dishes: dishes.filter((dish) => dish.dishCategoryIds.includes(category.id)),
    }))
    .filter((group) => group.dishes.length > 0);

/**
 * The whole public catalogue: dishes, menu types, categories, and the
 * size/topping tables needed to customize a dish. Shared by the browse-only
 * menu and the ordering flow, and unlike the old inline fetch it tracks
 * loading and failure — an empty list and a dead network used to look
 * identical.
 *
 * The menu is two levels deep: a menu type (breakfast, lunch, drinks) holds
 * categories, and a dish can sit in several of both.
 */
export const useMenuData = (restaurantId: string) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const enabled = Boolean(restaurantId);

  const [dishesQuery, categoriesQuery, toppingsQuery, sizesQuery, typesQuery] =
    useQueries({
      queries: [
        {
          queryKey: queryKeys.dishes(restaurantId),
          queryFn: () => getDishes(restaurantId),
          enabled,
        },
        {
          queryKey: queryKeys.categories(restaurantId),
          queryFn: () => getCategories(restaurantId),
          enabled,
        },
        {
          queryKey: queryKeys.toppings(restaurantId),
          queryFn: () => getToppings(restaurantId),
          enabled,
        },
        {
          queryKey: queryKeys.sizes(restaurantId),
          queryFn: () => getDishSizes(restaurantId),
          enabled,
        },
        {
          queryKey: queryKeys.menuTypes(restaurantId),
          queryFn: () => getMenuTypes(restaurantId),
          enabled,
        },
      ],
    });

  const dishes: Dish[] = dishesQuery.data ?? EMPTY_DISHES;
  const categories: DishCategory[] = categoriesQuery.data ?? EMPTY_CATEGORIES;
  const toppings: Topping[] = toppingsQuery.data ?? EMPTY_TOPPINGS;
  const sizes: DishSize[] = sizesQuery.data ?? EMPTY_SIZES;
  const menuTypes: MenuType[] = typesQuery.data ?? EMPTY_TYPES;

  const reload = useCallback(() => {
    dishesQuery.refetch();
    categoriesQuery.refetch();
    toppingsQuery.refetch();
    sizesQuery.refetch();
    typesQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // Menu types are excluded on purpose: without them the menu still browses as
  // one list, so their failure must not take the whole catalogue down with it.
  const error =
    dishesQuery.isError ||
    categoriesQuery.isError ||
    toppingsQuery.isError ||
    sizesQuery.isError;

  const loading =
    dishesQuery.isPending ||
    categoriesQuery.isPending ||
    toppingsQuery.isPending ||
    sizesQuery.isPending;

  /** Every category, across the whole menu. Used by the "all dishes" scope. */
  const groups = useMemo<MenuCategoryGroup[]>(
    () => buildCategoryGroups(categories, dishes, language),
    [categories, dishes, language],
  );

  const menuTypeGroups = useMemo<MenuTypeGroup[]>(
    () =>
      menuTypes
        .map((menuType) => {
          const typeDishes = dishes.filter((dish) =>
            (dish.dishMenuTypeIds ?? []).includes(menuType.id),
          );
          return {
            menuType,
            name: getTranslation(menuType.translations, language),
            dishes: typeDishes,
            categories: buildCategoryGroups(categories, typeDishes, language),
          };
        })
        // A menu type nobody has filed a dish under is not worth a tile.
        .filter((group) => group.dishes.length > 0),
    [menuTypes, categories, dishes, language],
  );

  return {
    dishes,
    categories,
    menuTypes,
    toppings,
    sizes,
    groups,
    menuTypeGroups,
    loading,
    error,
    reload,
  };
};
